import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {AgGridAngular} from "ag-grid-angular";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {ICharityBalance} from "../../../interfaces/charity/ICharityBalance";
import {CharityPayoutService} from "../../../services/charity-payout.service";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatButton} from "@angular/material/button";
import {PipesModule} from "../../../pipes/pipes.module";
import {
  CharityPayoutActionsComponent
} from "../../../components/charity/charity-payout-actions/charity-payout-actions.component";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {PermissionsService} from "../../../services/permissions.service";

@Component({
  selector: 'app-charity-balances-summary',
  standalone: true,
  templateUrl: './charity-balances-summary.component.html',
  imports: [
    AgGridAngular,
    LoadingCardContentComponent,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatButton,
    PipesModule
  ],
  styleUrls: ['./charity-balances-summary.component.scss']
})
export class CharityBalancesSummaryComponent implements OnInit {

  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe);
  private charityPayoutService: CharityPayoutService = inject(CharityPayoutService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private permissionService: PermissionsService = inject(PermissionsService);

  @Input() set gameData(gameP: IGameQueryResult) {
    this.activeGame = gameP;
    this.fetchBalances();
  }

  @Output() makePayoutClick: EventEmitter<string> = new EventEmitter<string>();

  public balancesGridApi!: GridApi<ICharityBalance>;
  public activeGame: IGameQueryResult | null = null;
  public charityBalances: ICharityBalance[] = [];
  public totalOwed: number = 0;
  public balanceColDefs: ColDef[] = [];

  uiState = UIStateEnum.ShowData;
  protected readonly UIStateEnum = UIStateEnum;

  ngOnInit(): void {
    this.initializeColumnDefs();
  }

  private initializeColumnDefs(): void {
    const baseColumns: ColDef[] = [
      {
        headerName: 'Charity Name',
        field: 'CharityName',
        resizable: true,
        sortable: true,
        flex: 2
      },
      {
        headerName: 'Total Contributions',
        field: 'TotalContributions',
        resizable: true,
        sortable: true,
        flex: 1,
        valueFormatter: params => {
          return this.currencyPipe.transform(params.value);
        }
      },
      {
        headerName: 'Total Payouts',
        field: 'TotalPayouts',
        resizable: true,
        sortable: true,
        flex: 1,
        valueFormatter: params => {
          return this.currencyPipe.transform(params.value);
        }
      },
      {
        headerName: 'Amount Owed',
        field: 'AmountOwed',
        resizable: true,
        sortable: true,
        sort: 'desc',
        flex: 1,
        valueFormatter: params => {
          return this.currencyPipe.transform(params.value);
        },
        cellStyle: params => {
          if (params.value > 0) {
            return {color: '#2e7d32', fontWeight: 'bold'};
          }
          return null;
        }
      }
    ];

    // Only add Actions column if user has ModifyCharityPayouts permission
    const hasModifyPermission = this.permissionService.userHasPermission(PermissionTypes.ModifyCharityPayouts);

    if (hasModifyPermission) {
      baseColumns.push({
        headerName: 'Actions',
        field: 'CharityId',
        resizable: true,
        flex: 1,
        cellRenderer: CharityPayoutActionsComponent,
        cellRendererParams: {
          onMakePayoutClick: (charityId: string) => {
            this.makePayoutClick.emit(charityId);
          }
        }
      });
    }

    this.balanceColDefs = baseColumns;
  }

  fetchBalances(): void {
    if (!this.activeGame) return;

    this.uiState = UIStateEnum.ShowLoading;
    this.charityPayoutService.fetchBalancesByGameId(this.activeGame.Id).subscribe({
      next: (balances) => {
        this.charityBalances = balances;
        this.calculateTotalOwed(balances);
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  calculateTotalOwed(balances: ICharityBalance[]): void {
    this.totalOwed = balances.reduce((sum, balance) => sum + balance.AmountOwed, 0);
  }

  onBalancesGridReady(params: GridReadyEvent<ICharityBalance>) {
    this.balancesGridApi = params.api;
    this.balancesGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }
}
